import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    Signal,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';

import { IonContent } from '@ionic/angular/standalone';

import { NgxOtpInputComponent, NgxOtpInputComponentOptions, NgxOtpStatus } from 'ngx-otp-input';

import { ButtonComponent, ErrorCardComponent, FooterComponent } from '../../components';
import { FormatPhoneNumberPipe } from '../../pipes';
import { AuthService, ClientUserService, ErrorService, ProUserService } from '../../services';
import { LoadingService } from '../../services/loading/loading.service';
import { RegisterService } from '../../services/register/register.service';
import { ToastService } from '../../services/toast/toast.service';
import { analyticsPageNames } from '../../shared';

@Component({
    selector: 'lib-otp-form',
    standalone: true,
    imports: [
        CommonModule,
        IonContent,
        NgxOtpInputComponent,
        ButtonComponent,
        FooterComponent,
        ErrorCardComponent,
        FormatPhoneNumberPipe
    ],
    templateUrl: './otp-form.component.html',
    styleUrl: './otp-form.component.css'
})
export class OtpFormComponent implements OnInit {
    /* eslint-disable */
    clientUserService = inject(ClientUserService);
    proUserService = inject(ProUserService);

    registerService = inject(RegisterService);
    loadingService = inject(LoadingService);
    toastService = inject(ToastService);
    authService = inject(AuthService);
    errorService = inject(ErrorService);

    @Input() title: string = '';
    @Input() type: 'register' | 'reset' = 'register';

    @Output() onContinueSuccess = new EventEmitter();
    @Output() onContinueError = new EventEmitter();

    @ViewChild('otpInput') otpInput!: NgxOtpInputComponent;

    forgotEmail: WritableSignal<string | null> = signal(null);

    initialCountdownSeconds: WritableSignal<number> = signal(60);
    countdownSeconds: WritableSignal<number> = signal(60);
    countdownFormatted: Signal<string> = computed(() => this.convertSeconds(this.countdownSeconds()));
    canContinue: WritableSignal<boolean> = signal(false);
    isResendingCode: WritableSignal<boolean> = signal(false);
    errors: WritableSignal<any> = signal(null);

    countdownInterval: any;

    otpStatus: NgxOtpStatus | null | undefined = undefined;
    otpValue: string = '';

    otpOptions: NgxOtpInputComponentOptions = {
        otpLength: 4
    };

    ngOnInit(): void {
        if (this.type === 'reset') {
            this.forgotEmail.set(this.authService.getForgotPasswordEmail());
        }
        this.initCountdown();
    }

    convertSeconds(s: number) {
        const min = Math.floor(s / 60);
        const minFormat = min > 0 ? min.toString() : min.toString().padStart(2, '0');
        const sec = s % 60;
        return minFormat + ':' + sec.toString().padStart(2, '0');
    }

    initCountdown() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        this.countdownInterval = setInterval(this.countdown, 1000);
    }

    countdown = () => {
        if (this.countdownSeconds() > 0) {
            this.countdownSeconds.set(this.countdownSeconds() - 1);
        } else {
            clearInterval(this.countdownInterval);
        }
    };

    onOTPChange(value: string[]) {
        this.errors.set(null);

        const digitCount = value.reduce((acc: number, currentValue: string) => {
            if (currentValue !== '') {
                acc++;
            }
            return acc;
        }, 0);

        this.canContinue.set(digitCount === 4);
    }

    onOTPComplete(value: string) {
        console.log(value);
        this.otpValue = value;
    }

    restartCountdown() {
        this.countdownSeconds = this.initialCountdownSeconds;
        this.initCountdown();
        this.resetForm();
    }

    resetForm() {
        this.otpInput.reset();
    }

    resendCode() {
        this.errors.set(null);

        if (this.isResendingCode()) return;

        this.loadingService.presentLoading();

        this.isResendingCode.set(true);

        if (this.type === 'register') {
            this.resendRegisterCode();
        } else if (this.type === 'reset') {
            this.resendResetCode();
        }
    }

    resendRegisterCode() {
        this.registerService.resendOtpCode().subscribe({
            next: data => {
                this.loadingService.dismissLoading();

                if (data.success === true) {
                    this.restartCountdown();

                    this.toastService.successToast({ message: 'OTP has been resent.' });
                } else {
                    this.errors.set(data.error);
                }
                this.isResendingCode.set(false);
            },
            error: error => {
                this.loadingService.dismissLoading();
                console.log(error);
                this.isResendingCode.set(false);
                this.errors = error.error;
            }
        });
    }

    resendResetCode() {
        if (!this.forgotEmail()) {
            this.toastService.errorToast({
                message: 'Please re-enter your email to resend your OTP.',
                duration: 5000
            });

            this.loadingService.dismissLoading();
            return;
        }

        this.authService.requestPasswordReset(this.forgotEmail() || '').subscribe({
            next: data => {
                this.isResendingCode.set(false);

                this.loadingService.dismissLoading();

                if (data['success'] && data['success'] === true) {
                    this.restartCountdown();

                    this.toastService.successToast({ message: 'OTP has been resent.' });
                } else {
                    this.errorService.apiErrors(analyticsPageNames.forgotPassword, data, { email: this.forgotEmail() });

                    this.errors.set(data.error);
                }
            },
            error: error => {
                this.isResendingCode.set(false);

                this.loadingService.dismissLoading();

                this.errors.set(error);

                const errorMessage = error.message ? error.message : error.statusText;

                this.errorService.apiErrors(analyticsPageNames.forgotPassword, errorMessage);
            }
        });
    }

    onContinue() {
        if (this.type === 'reset') {
            this.onContinueSuccess.emit(this.otpValue);
            return;
        }

        this.loadingService.presentLoading();

        this.registerService.verifyOtpCode(this.otpValue).subscribe({
            next: data => {
                this.loadingService.dismissLoading();
                if (data.success) {
                    this.onContinueSuccess.emit(data);
                } else {
                    this.onContinueError.emit(data.error);
                    this.errors.set(data.error);
                }
            },
            error: error => {
                this.onContinueError.emit(error);
                this.loadingService.dismissLoading();
                this.errors.set(error.error);
            }
        });
    }
}
